import {
  locationIdFormat,
  packingTemplateIdFormat,
  productIdFormat,
  storageLocationIdFormat,
  transferSessionIdFormat,
} from "@redotech/redo-model/ids";
import { packingMode } from "@redotech/redo-model/inventory/packing-mode";
import * as z from "zod/v4";

// Input schemas
// quantity: new items to add (creates new adjustment if > 0)
// executed: quantity to execute from existing adjustment
const TransferItemDestinationSchema_ = z.object({
  quantity: z.number().int().nonnegative().max(1_000_000).optional(),
  executed: z.number().int().nonnegative().max(1_000_000).optional(),
});
export interface TransferItemDestination
  extends z.infer<typeof TransferItemDestinationSchema_> {}

const TransferItemInputSchema_ = z.object({
  quantity: z.number().int().nonnegative().max(1_000_000).optional(),
  executed: z.number().int().nonnegative().max(1_000_000).optional(),
  lost: z.number().int().nonnegative().max(1_000_000).optional(),
  packingMode: z.enum(packingMode).optional(),
  packingTemplateId: packingTemplateIdFormat.schema.nullable().optional(),
  enteredQuantity: z.number().int().nonnegative().optional(),
  destinations: z
    .record(storageLocationIdFormat.schema, TransferItemDestinationSchema_)
    .optional(),
});
export interface TransferItemInput
  extends z.infer<typeof TransferItemInputSchema_> {}

const StorageLocationMapInputSchema_ = z.record(
  storageLocationIdFormat.schema,
  z.record(productIdFormat.schema, TransferItemInputSchema_),
);
export type StorageLocationMapInput = z.infer<
  typeof StorageLocationMapInputSchema_
>;

// Output schemas
const TransferItemStateSchema_ = z.object({
  quantity: z.number(),
  committed: z.number(),
  executed: z.number(),
  lost: z.number(),
});
export interface TransferItemState
  extends z.infer<typeof TransferItemStateSchema_> {}

const StorageLocationMapStateSchema_ = z.record(
  storageLocationIdFormat.schema,
  z.record(productIdFormat.schema, TransferItemStateSchema_),
);
export type StorageLocationMapState = z.infer<
  typeof StorageLocationMapStateSchema_
>;

const TransferErrorSchema_ = z.object({
  storageLocationId: storageLocationIdFormat.schema,
  productId: productIdFormat.schema,
  message: z.string(),
});
export interface TransferError extends z.infer<typeof TransferErrorSchema_> {}

export const TransferSessionStatusSchema = z.enum([
  "sourcing",
  "distributing",
  "completed",
]);
export type TransferSessionStatus = z.infer<typeof TransferSessionStatusSchema>;

const StorageLocationInfoSchema_ = z.object({
  name: z.string(),
  type: z.string(),
});
export interface StorageLocationInfo
  extends z.infer<typeof StorageLocationInfoSchema_> {}

const ProductInfoSchema_ = z.object({
  title: z.string(),
  sku: z.string().nullable(),
  imageUrl: z.string().nullable(),
  barcode: z.string().nullable(),
  barcodes: z.array(z.string()),
  caseBarcodes: z.array(z.string()).optional().default([]),
});
export interface ProductInfo extends z.infer<typeof ProductInfoSchema_> {}

const TransferSessionStateSchema_ = z.object({
  transferId: transferSessionIdFormat.schema,
  status: TransferSessionStatusSchema,
  source: StorageLocationMapStateSchema_,
  sourceOrder: z.array(storageLocationIdFormat.schema),
  destination: StorageLocationMapStateSchema_.optional(),
  destinationOrder: z.array(storageLocationIdFormat.schema).optional(),
  // Storage location metadata (id -> name, type) for display and unmapped detection
  storageLocationInfo: z
    .record(storageLocationIdFormat.schema, StorageLocationInfoSchema_)
    .optional(),
  // Product metadata (id -> title, sku, imageUrl) for display
  productInfo: z.record(productIdFormat.schema, ProductInfoSchema_).optional(),
  errors: z.array(TransferErrorSchema_),
});
export const TransferSessionStateSchema: z.ZodType<TransferSessionState> =
  TransferSessionStateSchema_;
export interface TransferSessionState
  extends z.infer<typeof TransferSessionStateSchema_> {}

// POST - Create session with initial sources

const ReceivingItemSchema_ = z.object({
  productId: productIdFormat.schema,
  quantity: z.number().int().positive(),
});

const TransferVariantSchema_ = z
  .object({
    locationId: locationIdFormat.schema,
    type: z.literal("transfer"),
    source: StorageLocationMapInputSchema_,
    sourceOrder: z.array(storageLocationIdFormat.schema),
    referenceId: z.string().max(500).optional(),
  })
  .strict();

const ReceivingVariantSchema_ = z
  .object({
    locationId: locationIdFormat.schema,
    type: z.literal("receiving"),
    items: z.array(ReceivingItemSchema_).min(1),
    referenceId: z.string().max(500).optional(),
  })
  .strict();

const BackwardCompatTransferSchema_ = z
  .object({
    locationId: locationIdFormat.schema,
    source: StorageLocationMapInputSchema_,
    sourceOrder: z.array(storageLocationIdFormat.schema),
    referenceId: z.string().max(500).optional(),
  })
  .strict();

const CreateTransferSessionInputSchema_ = z.union([
  z.discriminatedUnion("type", [
    TransferVariantSchema_,
    ReceivingVariantSchema_,
  ]),
  BackwardCompatTransferSchema_,
]);

export const createTransferSession = {
  name: "createTransferSession" as const,
  input: CreateTransferSessionInputSchema_,
  output: TransferSessionStateSchema,
};

export type CreateTransferSessionInput = z.infer<
  typeof CreateTransferSessionInputSchema_
>;

// PATCH - Add sources OR add destinations, optionally complete phase
export const updateTransferSession = {
  name: "updateTransferSession" as const,
  input: z.object({
    transferId: transferSessionIdFormat.schema,
    // Sourcing phase - add more sources
    source: StorageLocationMapInputSchema_.optional(),
    sourceOrder: z.array(storageLocationIdFormat.schema).optional(),
    // Distribution phase - add destinations
    destination: StorageLocationMapInputSchema_.optional(),
    destinationOrder: z.array(storageLocationIdFormat.schema).optional(),
    // Explicit completion: "distributing" completes sourcing, "completed" completes distributing
    state: z.enum(["distributing", "completed"]).optional(),
  }),
  output: TransferSessionStateSchema,
};

export type UpdateTransferSessionInput = z.infer<
  typeof updateTransferSession.input
>;

// GET - Retrieve current session state
export const getTransferSessionState = {
  name: "getTransferSessionState" as const,
  input: z.object({ transferId: transferSessionIdFormat.schema }),
  output: TransferSessionStateSchema,
};

export type GetTransferSessionStateInput = z.infer<
  typeof getTransferSessionState.input
>;

export { type TransferSessionId } from "@redotech/redo-model/ids";
